import classNames from "classnames";
import React, { useState } from "react";
import { useEffect } from "react";
import { UseFormRegisterReturn, FieldError, Merge, FieldErrors } from "react-hook-form";
import { MdClear, MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";
import { getUrlPathname } from "../../services/getUrlPathname";
import { Container } from "../Container/Container";
import { Flex } from "../Flex/Flex";
import { Row } from "../Row/Row";
import { Stack } from "../Stack/Stack";
import styles from "./TextField.module.scss";

export interface TextFieldProps extends React.ComponentPropsWithoutRef<"input"> {
  name: string;
  label: string;
  requiresActivation?: boolean;
  defaultValue?: string;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  error?: FieldError | Merge<FieldError, FieldErrors<any>>;
  register?: UseFormRegisterReturn;
  expandTrailing?: boolean;
  withoutBorder?: boolean;
  onClear?(): void;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(function Field(
  {
    type,
    name,
    value,
    label,
    requiresActivation,
    defaultValue,
    leading,
    trailing,
    error,
    register,
    expandTrailing, // used only by EditableField
    withoutBorder,
    className,
    onClear,
    onClick,
    onChange,
    onFocus,
    onBlur,
    ...rest
  },
  ref,
) {
  const [hasValue, setHasValue] = useState(typeof defaultValue === "string" && defaultValue.length > 0);
  const [hasFocus, setHasFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isActivated, setIsActivated] = useState(requiresActivation === true ? false : true);

  const handleValueChange = (value: string) => {
    const hasValueNow = value.length > 0;

    // only update state if having value has changed
    if (hasValueNow !== hasValue) {
      setHasValue(hasValueNow);
    }
  };

  // update having value
  useEffect(() => {
    if (typeof value !== "string") {
      return;
    }

    const nowHasValue = value.length > 0;

    if (nowHasValue !== hasValue) {
      setHasValue(nowHasValue);
    }
  }, [hasValue, value]);

  // toggle password button (both show and hide icons are shown on top of each other and faded in/out)
  const togglePassword = (
    <Stack>
      <Container center>
        <MdOutlineVisibilityOff
          className={classNames(styles["toggle-password"], { [styles["toggle-password--visible"]]: showPassword })}
        />
      </Container>
      <Container center>
        <MdOutlineVisibility
          className={classNames(styles["toggle-password"], { [styles["toggle-password--visible"]]: !showPassword })}
        />
      </Container>
    </Stack>
  );

  const clearButton = <MdClear title="Clear" className={styles["clear-icon"]} onClick={onClear} />;

  // use password toggle for password fields when custom trailing element has not been set
  const usePasswordToggle = trailing === undefined && type === "password";
  const useTrailing = usePasswordToggle ? togglePassword : onClear ? clearButton : trailing;

  // called when trailing icon container is clicked (larger click area)
  const onTrailingClick = () => {
    if (usePasswordToggle) {
      setShowPassword(!showPassword);
    }
  };

  const constraintsIdentifier = `${name}-constraints`;

  const isFileInput = type === "file";

  return (
    <Flex
      column
      ref={ref}
      className={classNames(
        styles.field,
        {
          [styles["field--has-value"]]: hasValue,
          [styles["field--has-focus"]]: hasFocus,
          [styles["field--has-error"]]: error !== undefined,
          [styles["field--has-leading"]]: leading !== undefined,
          [styles["field--deactivated"]]: !isActivated,
          [styles["field--file"]]: isFileInput,
        },
        className,
      )}
    >
      <label htmlFor={name} className={styles.label}>
        {label}
      </label>
      <Row>
        {leading !== undefined && (
          <Container className={classNames(styles["icon"], styles["icon--leading"])}>{leading}</Container>
        )}
        <Container expanded relative>
          <input
            id={name}
            type={showPassword ? "text" : type}
            name={name}
            value={value}
            defaultValue={defaultValue}
            aria-describedby={constraintsIdentifier}
            onClick={(e) => {
              setIsActivated(true);

              if (onClick) {
                onClick(e);
              }
            }}
            onChange={(e) => {
              handleValueChange(e.target.value);

              if (onChange) {
                onChange(e);
              }
            }}
            onFocus={(e) => {
              setHasFocus(true);

              if (onFocus) {
                onFocus(e);
              }
            }}
            onBlur={(e) => {
              setHasFocus(false);

              if (onBlur) {
                onBlur(e);
              }
            }}
            className={styles.input}
            {...register}
            {...rest}
          />
          {isFileInput && typeof value === "string" && value.length > 0 && (
            <Container overlay ignorePointerEvents small darker mainAxisAlignment="center">
              {getUrlPathname(value)}
            </Container>
          )}
        </Container>
        {useTrailing !== undefined && (
          <Row
            className={classNames(styles["icon"], styles["icon--trailing"], {
              [styles["icon--expand-trailing"]]: expandTrailing,
            })}
            onClick={onTrailingClick}
            aria-label={
              usePasswordToggle ? (showPassword ? "Hide password" : "Show password as plain text") : undefined
            }
          >
            {useTrailing}
          </Row>
        )}
      </Row>
      <div
        className={classNames(styles.border, {
          [styles["border--transparent"]]: withoutBorder,
        })}
      ></div>
      <div id={constraintsIdentifier} className={styles.error}>
        {error && error.message}
      </div>
    </Flex>
  );
});
